// Wormhole bonus-game portal: a large swirling vortex (purple/cyan,
// force-field style) that slowly rotates and pulses while the bonus game
// runs. Implemented as a single shader quad in the same style as
// spawn-portals.ts / zone-effect.ts; all animation derives from the sim
// tick via uTime, so identical runs render identical portals.

import * as THREE from 'three';
import type { Vec2 } from '../types.js';

// World-space quad size; the vortex disc sits well inside it. Big enough to
// read as a hazard at gameplay distance — its pull field reaches ~2x wider.
const WORMHOLE_SIZE = 13;

export class WormholePortal {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.ShaderMaterial;

  /** Portal hues; the entrance wormhole uses the purple/cyan defaults, the
   * subspace exit portal passes mint/cyan so the escape hatch reads at a
   * glance (matches the HUD's green exit wedge). */
  constructor(colors?: { a: number; b: number }) {
    const geometry = new THREE.PlaneGeometry(1, 1);
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        // Sim seconds (world.tick / 60); keeps the swirl deterministic.
        uTime: { value: 0 },
        // 0..1 open envelope driven by the renderer's tick-based fade.
        uStrength: { value: 0 },
        uColorA: { value: new THREE.Color(colors?.a ?? 0xb45cff) },
        uColorB: { value: new THREE.Color(colors?.b ?? 0x4de3ff) },
      },
      vertexShader: WORMHOLE_VERT,
      fragmentShader: WORMHOLE_FRAG,
      transparent: true,
      depthTest: false,
      depthWrite: false,
      blending: THREE.CustomBlending,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneFactor,
      blendEquation: THREE.AddEquation
    });

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.frustumCulled = false;
    // Above the bonus tint and boundary wall, below spawn portals.
    this.mesh.renderOrder = 3;
    this.mesh.visible = false;
    this.mesh.scale.set(WORMHOLE_SIZE, WORMHOLE_SIZE, 1);
  }

  /**
   * Position and animate the portal. `pos` is the render-frame position
   * (already unwrapped to the camera anchor), `time` is sim seconds,
   * `strength` the 0..1 open envelope. `collapse01` (0..1) runs the portal
   * implosion: the disc drains into its own throat as it fades. Hidden when
   * strength bottoms out.
   */
  update(pos: Vec2, time: number, strength: number, collapse01 = 0): void {
    if (strength <= 0.004) {
      this.mesh.visible = false;
      this.material.uniforms.uStrength.value = 0;
      return;
    }
    this.mesh.visible = true;
    this.mesh.position.set(pos.x, pos.y, 0.03);
    const shrink = 1 - 0.8 * collapse01;
    this.mesh.scale.set(WORMHOLE_SIZE * shrink, WORMHOLE_SIZE * shrink, 1);
    this.material.uniforms.uTime.value = time;
    this.material.uniforms.uStrength.value = strength * (1 - collapse01);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}

const WORMHOLE_VERT = /* glsl */ `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const WORMHOLE_FRAG = /* glsl */ `
precision highp float;

uniform float uTime;
uniform float uStrength;
uniform vec3 uColorA;
uniform vec3 uColorB;
varying vec2 vUv;

void main() {
  float env = uStrength;
  if (env <= 0.004) discard;

  // -1..1 across the quad.
  vec2 p = vUv * 2.0 - 1.0;
  float rad = length(p);
  float th = atan(p.y, p.x);

  // Slow overall rotation; the swirl winds tighter toward the rim and drifts
  // inward over time, so the disc reads as a vortex rather than a flat spin.
  float swirl = th + uTime * 0.35 + rad * 4.0 - uTime * 0.6;

  // Alternating purple/cyan spiral arms; the center stays calm.
  float arms = 0.5 + 0.5 * sin(swirl * 2.0);
  vec3 armColor = mix(uColorA, uColorB, arms);
  float armMask = smoothstep(0.12, 0.55, rad) * (1.0 - smoothstep(0.72, 0.95, rad));
  float body = (0.35 + 0.65 * arms) * armMask * 0.55;

  // Concentric force-field rings drifting inward toward the throat.
  float f = fract(rad * 5.0 + uTime * 0.9);
  float rings = (1.0 - smoothstep(0.0, 0.14, min(f, 1.0 - f))) * armMask * 0.2;

  // Pulsing mouth rim where the field meets normal space. Crisp and a touch
  // wider than the soft arms: the neon rim ring is what keeps the vortex in
  // the game's wireframe language instead of a muddy blur at distance.
  float pulse = 0.85 + 0.15 * sin(uTime * 2.2);
  float rim = 1.0 - smoothstep(0.0, 0.055, abs(rad - 0.9));
  vec3 rimColor = mix(uColorA, vec3(1.0), 0.5) * 1.35;

  // Hot core at the throat.
  float core = exp(-7.0 * rad * rad) * 1.1;
  vec3 coreColor = vec3(1.0, 0.92, 1.0);

  vec3 col = armColor * (body + rings) + rimColor * rim + coreColor * core;
  col *= env * pulse;
  // Soft radial clip so the quad edge never shows.
  col *= 1.0 - smoothstep(0.92, 1.0, rad);
  if (max(col.r, max(col.g, col.b)) <= 0.004) discard;
  gl_FragColor = vec4(col, 1.0);
}
`;
